/**
 * Tester Management Dashboard
 *
 * Features:
 * - Dashboard with KPIs and critical alerts
 * - Tester inventory management with inline editing
 * - Tester order workflow with bulk approval
 * - Ratio configuration per category/brand/store
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Tune as RatioIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  FileDownload as ExportIcon,
  CheckCircle as ApproveIcon,
  AutoFixHigh as GenerateIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface TesterDashboardData {
  total_testers_in_stock: number;
  zero_stock_stores: number;
  low_stock_stores: number;
  pending_orders: number;
}

interface TesterAlert {
  alert_id: string;
  store_name: string;
  product_name: string;
  sku: string;
  category: string;
  current_testers: number;
  sellable_stock: number;
  ratio: string;
  needed: number;
}

interface TesterInventoryItem {
  store_id: string;
  store_name: string;
  product_id: string;
  product_name: string;
  sku: string;
  category: string;
  brand: string;
  current_tester_stock: number;
  optimal_stock: number;
  status: 'adequate' | 'low' | 'zero' | 'excess';
  last_replaced: string | null;
}

interface TesterOrder {
  order_id: string;
  store_name: string;
  product_name: string;
  sku: string;
  current_stock: number;
  needed: number;
  suggested_quantity: number;
  status: 'pending' | 'approved' | 'transmitted' | 'delivered';
}

interface TesterRatio {
  ratio_id: string;
  category: string;
  brand: string;
  store_name: string;
  ratio_n: number;
  min_per_store: number;
  max_per_store: number;
  priority: number;
  is_active: boolean;
}

interface RatioFormData {
  category: string;
  brand: string;
  store_name: string;
  ratio_n: number;
  min_per_store: number;
  max_per_store: number;
  priority: number;
  is_active: boolean;
}

// ---------------------------------------------------------------------------
// TabPanel helper
// ---------------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// ---------------------------------------------------------------------------
// Default ratio form
// ---------------------------------------------------------------------------

const defaultRatioForm: RatioFormData = {
  category: '',
  brand: '',
  store_name: '',
  ratio_n: 10,
  min_per_store: 1,
  max_per_store: 5,
  priority: 1,
  is_active: true,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TesterManagementDashboard: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Notification state
  const [notification, setNotification] = useState<{
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // ---------------------------------------------------------------------------
  // TAB 1 - Dashboard state & queries
  // ---------------------------------------------------------------------------

  const {
    data: dashboardData,
    isLoading: dashboardLoading,
  } = useQuery<TesterDashboardData>({
    queryKey: ['testerDashboard'],
    queryFn: async () => {
      const response = await api.getTesterDashboard();
      return response.data;
    },
  });

  const {
    data: alerts,
    isLoading: alertsLoading,
  } = useQuery<TesterAlert[]>({
    queryKey: ['testerAlerts'],
    queryFn: async () => {
      const response = await api.getTesterAlerts();
      return response.data;
    },
  });

  const generateOrdersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.generateTesterOrders();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerOrders'] });
      queryClient.invalidateQueries({ queryKey: ['testerDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['testerAlerts'] });
      setNotification({ message: t('tester.ordersGenerated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorGeneratingOrders'),
        severity: 'error',
      });
    },
  });

  // ---------------------------------------------------------------------------
  // TAB 2 - Inventory state & queries
  // ---------------------------------------------------------------------------

  const [inventoryFilters, setInventoryFilters] = useState({
    store: '',
    category: '',
    status: '',
  });

  const [editingCell, setEditingCell] = useState<{
    storeId: string;
    productId: string;
  } | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  const {
    data: inventoryData,
    isLoading: inventoryLoading,
  } = useQuery<TesterInventoryItem[]>({
    queryKey: ['testerInventory', inventoryFilters],
    queryFn: async () => {
      const params: any = {};
      if (inventoryFilters.store) params.store = inventoryFilters.store;
      if (inventoryFilters.category) params.category = inventoryFilters.category;
      if (inventoryFilters.status) params.status = inventoryFilters.status;
      const response = await api.getTesterInventory(params);
      return response.data;
    },
  });

  const updateStockMutation = useMutation({
    mutationFn: async ({
      storeId,
      productId,
      data,
    }: {
      storeId: string;
      productId: string;
      data: { current_tester_stock: number };
    }) => {
      const response = await api.updateTesterStock(storeId, productId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerInventory'] });
      queryClient.invalidateQueries({ queryKey: ['testerDashboard'] });
      setEditingCell(null);
      setNotification({ message: t('tester.stockUpdated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorUpdatingStock'),
        severity: 'error',
      });
    },
  });

  // Unique values for inventory filter dropdowns
  const inventoryStores = useMemo(() => {
    if (!inventoryData) return [];
    return [...new Set(inventoryData.map((item) => item.store_name))].sort();
  }, [inventoryData]);

  const inventoryCategories = useMemo(() => {
    if (!inventoryData) return [];
    return [...new Set(inventoryData.map((item) => item.category))].sort();
  }, [inventoryData]);

  const statusOptions = ['adequate', 'low', 'zero', 'excess'];

  const handleStartEdit = (item: TesterInventoryItem) => {
    setEditingCell({ storeId: item.store_id, productId: item.product_id });
    setEditValue(item.current_tester_stock);
  };

  const handleSaveEdit = () => {
    if (!editingCell) return;
    updateStockMutation.mutate({
      storeId: editingCell.storeId,
      productId: editingCell.productId,
      data: { current_tester_stock: editValue },
    });
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  // ---------------------------------------------------------------------------
  // TAB 3 - Orders state & queries
  // ---------------------------------------------------------------------------

  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  const {
    data: ordersData,
    isLoading: ordersLoading,
  } = useQuery<TesterOrder[]>({
    queryKey: ['testerOrders'],
    queryFn: async () => {
      const response = await api.getTesterOrders({});
      return response.data;
    },
  });

  const bulkApproveMutation = useMutation({
    mutationFn: async (orderIds: string[]) => {
      const response = await api.bulkApproveTesterOrders(orderIds);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerOrders'] });
      queryClient.invalidateQueries({ queryKey: ['testerDashboard'] });
      setSelectedOrderIds(new Set());
      setNotification({ message: t('tester.ordersApproved'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorApprovingOrders'),
        severity: 'error',
      });
    },
  });

  const approveOrderMutation = useMutation({
    mutationFn: async ({ orderId, quantity }: { orderId: string; quantity: number }) => {
      const response = await api.approveTesterOrder(orderId, quantity);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerOrders'] });
      queryClient.invalidateQueries({ queryKey: ['testerDashboard'] });
      setNotification({ message: t('tester.orderApproved'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorApprovingOrder'),
        severity: 'error',
      });
    },
  });

  // Checkbox handlers for orders
  const isAllOrdersSelected =
    ordersData && ordersData.length > 0 && ordersData.every((o) => selectedOrderIds.has(o.order_id));
  const isSomeOrdersSelected = ordersData && ordersData.some((o) => selectedOrderIds.has(o.order_id));

  const handleSelectAllOrders = () => {
    if (!ordersData) return;
    if (isAllOrdersSelected) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(ordersData.map((o) => o.order_id)));
    }
  };

  const handleToggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleBulkApprove = () => {
    if (selectedOrderIds.size === 0) return;
    bulkApproveMutation.mutate(Array.from(selectedOrderIds));
  };

  // ---------------------------------------------------------------------------
  // TAB 4 - Ratio Configuration state & queries
  // ---------------------------------------------------------------------------

  const [ratioDialogOpen, setRatioDialogOpen] = useState(false);
  const [editingRatio, setEditingRatio] = useState<TesterRatio | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ratioToDelete, setRatioToDelete] = useState<string | null>(null);
  const [ratioFormData, setRatioFormData] = useState<RatioFormData>(defaultRatioForm);

  const {
    data: ratiosData,
    isLoading: ratiosLoading,
  } = useQuery<TesterRatio[]>({
    queryKey: ['testerRatios'],
    queryFn: async () => {
      const response = await api.getTesterRatios();
      return response.data;
    },
  });

  const createRatioMutation = useMutation({
    mutationFn: async (data: RatioFormData) => {
      const response = await api.createTesterRatio(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerRatios'] });
      handleCloseRatioDialog();
      setNotification({ message: t('tester.ratioCreated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorCreatingRatio'),
        severity: 'error',
      });
    },
  });

  const updateRatioMutation = useMutation({
    mutationFn: async ({ ratioId, data }: { ratioId: string; data: RatioFormData }) => {
      const response = await api.updateTesterRatio(ratioId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerRatios'] });
      handleCloseRatioDialog();
      setNotification({ message: t('tester.ratioUpdated'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorUpdatingRatio'),
        severity: 'error',
      });
    },
  });

  const deleteRatioMutation = useMutation({
    mutationFn: async (ratioId: string) => {
      const response = await api.deleteTesterRatio(ratioId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testerRatios'] });
      setDeleteConfirmOpen(false);
      setRatioToDelete(null);
      setNotification({ message: t('tester.ratioDeleted'), severity: 'success' });
    },
    onError: (error: any) => {
      setNotification({
        message: error.response?.data?.detail || t('tester.errorDeletingRatio'),
        severity: 'error',
      });
    },
  });

  const handleOpenCreateRatio = () => {
    setEditingRatio(null);
    setRatioFormData(defaultRatioForm);
    setRatioDialogOpen(true);
  };

  const handleOpenEditRatio = (ratio: TesterRatio) => {
    setEditingRatio(ratio);
    setRatioFormData({
      category: ratio.category,
      brand: ratio.brand,
      store_name: ratio.store_name,
      ratio_n: ratio.ratio_n,
      min_per_store: ratio.min_per_store,
      max_per_store: ratio.max_per_store,
      priority: ratio.priority,
      is_active: ratio.is_active,
    });
    setRatioDialogOpen(true);
  };

  const handleCloseRatioDialog = () => {
    setRatioDialogOpen(false);
    setEditingRatio(null);
    setRatioFormData(defaultRatioForm);
  };

  const handleSaveRatio = () => {
    if (editingRatio) {
      updateRatioMutation.mutate({ ratioId: editingRatio.ratio_id, data: ratioFormData });
    } else {
      createRatioMutation.mutate(ratioFormData);
    }
  };

  const handleConfirmDeleteRatio = (ratioId: string) => {
    setRatioToDelete(ratioId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteRatio = () => {
    if (!ratioToDelete) return;
    deleteRatioMutation.mutate(ratioToDelete);
  };

  // ---------------------------------------------------------------------------
  // Status chip helpers
  // ---------------------------------------------------------------------------

  const getInventoryStatusChip = (status: string) => {
    const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
      adequate: 'success',
      low: 'warning',
      zero: 'error',
      excess: 'info',
    };
    return (
      <Chip
        label={t(`tester.status_${status}`)}
        color={colorMap[status] || 'default'}
        size="small"
      />
    );
  };

  const getOrderStatusChip = (status: string) => {
    const colorMap: Record<string, 'default' | 'success' | 'info' | 'primary'> = {
      pending: 'default',
      approved: 'success',
      transmitted: 'info',
      delivered: 'primary',
    };
    return (
      <Chip
        label={t(`tester.orderStatus_${status}`)}
        color={colorMap[status] || 'default'}
        size="small"
      />
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('tester.title')}
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<DashboardIcon />} iconPosition="start" label={t('tester.dashboard')} />
          <Tab icon={<InventoryIcon />} iconPosition="start" label={t('tester.inventory')} />
          <Tab icon={<OrdersIcon />} iconPosition="start" label={t('tester.orders')} />
          <Tab icon={<RatioIcon />} iconPosition="start" label={t('tester.ratioConfig')} />
        </Tabs>
      </Paper>

      {/* ================================================================= */}
      {/* TAB 1 - Dashboard                                                 */}
      {/* ================================================================= */}
      <TabPanel value={tabValue} index={0}>
        {dashboardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('tester.totalTestersInStock')}
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData?.total_testers_in_stock ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, backgroundColor: '#ffebee' }}>
                  <Box>
                    <Typography variant="subtitle2" color="error.main">
                      {t('tester.zeroStockStores')}
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {dashboardData?.zero_stock_stores ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, backgroundColor: '#fff8e1' }}>
                  <Box>
                    <Typography variant="subtitle2" color="warning.main">
                      {t('tester.lowStockStores')}
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {dashboardData?.low_stock_stores ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('tester.pendingOrders')}
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData?.pending_orders ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Critical Alerts Table */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <WarningIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                  {t('tester.criticalAlerts')}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<GenerateIcon />}
                  onClick={() => generateOrdersMutation.mutate()}
                  disabled={generateOrdersMutation.isPending}
                >
                  {generateOrdersMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    t('tester.generateOrders')
                  )}
                </Button>
              </Box>

              {alertsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('tester.store')}</TableCell>
                        <TableCell>{t('tester.product')}</TableCell>
                        <TableCell>{t('tester.sku')}</TableCell>
                        <TableCell>{t('tester.category')}</TableCell>
                        <TableCell align="right">{t('tester.currentTesters')}</TableCell>
                        <TableCell align="right">{t('tester.sellableStock')}</TableCell>
                        <TableCell align="center">{t('tester.ratio')}</TableCell>
                        <TableCell align="right">{t('tester.needed')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts && alerts.length > 0 ? (
                        alerts.map((alert) => (
                          <TableRow key={alert.alert_id} hover>
                            <TableCell>{alert.store_name}</TableCell>
                            <TableCell>{alert.product_name}</TableCell>
                            <TableCell>{alert.sku}</TableCell>
                            <TableCell>{alert.category}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={alert.current_testers}
                                color={alert.current_testers === 0 ? 'error' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{alert.sellable_stock}</TableCell>
                            <TableCell align="center">{alert.ratio}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="bold" color="error.main">
                                {alert.needed}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                              {t('tester.noAlerts')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </TabPanel>

      {/* ================================================================= */}
      {/* TAB 2 - Tester Inventory                                          */}
      {/* ================================================================= */}
      <TabPanel value={tabValue} index={1}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tester.filterStore')}</InputLabel>
                <Select
                  value={inventoryFilters.store}
                  label={t('tester.filterStore')}
                  onChange={(e) =>
                    setInventoryFilters((prev) => ({ ...prev, store: e.target.value }))
                  }
                >
                  <MenuItem value="">{t('tester.all')}</MenuItem>
                  {inventoryStores.map((store) => (
                    <MenuItem key={store} value={store}>
                      {store}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tester.filterCategory')}</InputLabel>
                <Select
                  value={inventoryFilters.category}
                  label={t('tester.filterCategory')}
                  onChange={(e) =>
                    setInventoryFilters((prev) => ({ ...prev, category: e.target.value }))
                  }
                >
                  <MenuItem value="">{t('tester.all')}</MenuItem>
                  {inventoryCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tester.filterStatus')}</InputLabel>
                <Select
                  value={inventoryFilters.status}
                  label={t('tester.filterStatus')}
                  onChange={(e) =>
                    setInventoryFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <MenuItem value="">{t('tester.all')}</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`tester.status_${status}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={1}>
              <Tooltip title={t('tester.exportExcel')}>
                <Button variant="outlined" startIcon={<ExportIcon />} fullWidth>
                  {t('tester.export')}
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>

        {/* Inventory Table */}
        <Paper sx={{ p: 2 }}>
          {inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tester.store')}</TableCell>
                    <TableCell>{t('tester.product')}</TableCell>
                    <TableCell>{t('tester.sku')}</TableCell>
                    <TableCell>{t('tester.category')}</TableCell>
                    <TableCell>{t('tester.brand')}</TableCell>
                    <TableCell align="right">{t('tester.currentTesters')}</TableCell>
                    <TableCell align="right">{t('tester.optimal')}</TableCell>
                    <TableCell align="center">{t('tester.statusLabel')}</TableCell>
                    <TableCell>{t('tester.lastReplaced')}</TableCell>
                    <TableCell align="center">{t('tester.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryData && inventoryData.length > 0 ? (
                    inventoryData.map((item) => {
                      const isEditing =
                        editingCell?.storeId === item.store_id &&
                        editingCell?.productId === item.product_id;
                      return (
                        <TableRow key={`${item.store_id}-${item.product_id}`} hover>
                          <TableCell>{item.store_name}</TableCell>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell>{item.sku}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell>{item.brand}</TableCell>
                          <TableCell align="right">
                            {isEditing ? (
                              <TextField
                                type="number"
                                size="small"
                                value={editValue}
                                onChange={(e) => setEditValue(parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, style: { width: 60 } }}
                                autoFocus
                              />
                            ) : (
                              item.current_tester_stock
                            )}
                          </TableCell>
                          <TableCell align="right">{item.optimal_stock}</TableCell>
                          <TableCell align="center">{getInventoryStatusChip(item.status)}</TableCell>
                          <TableCell>
                            {item.last_replaced
                              ? new Date(item.last_replaced).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {isEditing ? (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handleSaveEdit}
                                  disabled={updateStockMutation.isPending}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleCancelEdit}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleStartEdit(item)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          {t('tester.noInventoryData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* ================================================================= */}
      {/* TAB 3 - Tester Orders                                             */}
      {/* ================================================================= */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('tester.ordersList')}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedOrderIds.size > 0 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleBulkApprove}
                  disabled={bulkApproveMutation.isPending}
                >
                  {bulkApproveMutation.isPending ? (
                    <CircularProgress size={20} />
                  ) : (
                    `${t('tester.bulkApprove')} (${selectedOrderIds.size})`
                  )}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<GenerateIcon />}
                onClick={() => generateOrdersMutation.mutate()}
                disabled={generateOrdersMutation.isPending}
              >
                {generateOrdersMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  t('tester.generateOrders')
                )}
              </Button>
            </Box>
          </Box>

          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isSomeOrdersSelected && !isAllOrdersSelected}
                        checked={!!isAllOrdersSelected}
                        onChange={handleSelectAllOrders}
                      />
                    </TableCell>
                    <TableCell>{t('tester.store')}</TableCell>
                    <TableCell>{t('tester.product')}</TableCell>
                    <TableCell>{t('tester.sku')}</TableCell>
                    <TableCell align="right">{t('tester.currentStock')}</TableCell>
                    <TableCell align="right">{t('tester.needed')}</TableCell>
                    <TableCell align="right">{t('tester.suggestedQty')}</TableCell>
                    <TableCell align="center">{t('tester.statusLabel')}</TableCell>
                    <TableCell align="center">{t('tester.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersData && ordersData.length > 0 ? (
                    ordersData.map((order) => (
                      <TableRow key={order.order_id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedOrderIds.has(order.order_id)}
                            onChange={() => handleToggleOrderSelection(order.order_id)}
                          />
                        </TableCell>
                        <TableCell>{order.store_name}</TableCell>
                        <TableCell>{order.product_name}</TableCell>
                        <TableCell>{order.sku}</TableCell>
                        <TableCell align="right">{order.current_stock}</TableCell>
                        <TableCell align="right">{order.needed}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">{order.suggested_quantity}</Typography>
                        </TableCell>
                        <TableCell align="center">{getOrderStatusChip(order.status)}</TableCell>
                        <TableCell align="center">
                          {order.status === 'pending' && (
                            <Tooltip title={t('tester.approveOrder')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() =>
                                  approveOrderMutation.mutate({
                                    orderId: order.order_id,
                                    quantity: order.suggested_quantity,
                                  })
                                }
                                disabled={approveOrderMutation.isPending}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          {t('tester.noOrders')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* ================================================================= */}
      {/* TAB 4 - Ratio Configuration                                       */}
      {/* ================================================================= */}
      <TabPanel value={tabValue} index={3}>
        {/* Default ratios info */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {t('tester.defaultRatios')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="Parfum 1:8" color="info" variant="outlined" />
            <Chip label="Maquillage 1:10" color="info" variant="outlined" />
            <Chip label="Skincare 1:12" color="info" variant="outlined" />
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('tester.ratioList')}</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateRatio}
            >
              {t('tester.addRatio')}
            </Button>
          </Box>

          {ratiosLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('tester.category')}</TableCell>
                    <TableCell>{t('tester.brand')}</TableCell>
                    <TableCell>{t('tester.store')}</TableCell>
                    <TableCell align="center">{t('tester.ratioLabel')}</TableCell>
                    <TableCell align="right">{t('tester.minPerStore')}</TableCell>
                    <TableCell align="right">{t('tester.maxPerStore')}</TableCell>
                    <TableCell align="right">{t('tester.priority')}</TableCell>
                    <TableCell align="center">{t('tester.active')}</TableCell>
                    <TableCell align="center">{t('tester.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ratiosData && ratiosData.length > 0 ? (
                    ratiosData.map((ratio) => (
                      <TableRow key={ratio.ratio_id} hover>
                        <TableCell>{ratio.category}</TableCell>
                        <TableCell>{ratio.brand}</TableCell>
                        <TableCell>{ratio.store_name || t('tester.allStores')}</TableCell>
                        <TableCell align="center">
                          <Chip label={`1:${ratio.ratio_n}`} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{ratio.min_per_store}</TableCell>
                        <TableCell align="right">{ratio.max_per_store}</TableCell>
                        <TableCell align="right">{ratio.priority}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ratio.is_active ? t('tester.yes') : t('tester.no')}
                            color={ratio.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditRatio(ratio)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleConfirmDeleteRatio(ratio.ratio_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          {t('tester.noRatios')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* ================================================================= */}
      {/* Ratio Create/Edit Dialog                                          */}
      {/* ================================================================= */}
      <Dialog open={ratioDialogOpen} onClose={handleCloseRatioDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRatio ? t('tester.editRatio') : t('tester.addRatio')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('tester.category')}
                fullWidth
                value={ratioFormData.category}
                onChange={(e) =>
                  setRatioFormData((prev) => ({ ...prev, category: e.target.value }))
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('tester.brand')}
                fullWidth
                value={ratioFormData.brand}
                onChange={(e) =>
                  setRatioFormData((prev) => ({ ...prev, brand: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('tester.store')}
                fullWidth
                value={ratioFormData.store_name}
                onChange={(e) =>
                  setRatioFormData((prev) => ({ ...prev, store_name: e.target.value }))
                }
                helperText={t('tester.storeHelperText')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('tester.ratioN')}
                type="number"
                fullWidth
                value={ratioFormData.ratio_n}
                onChange={(e) =>
                  setRatioFormData((prev) => ({
                    ...prev,
                    ratio_n: parseInt(e.target.value) || 1,
                  }))
                }
                inputProps={{ min: 1 }}
                helperText={t('tester.ratioNHelper')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('tester.minPerStore')}
                type="number"
                fullWidth
                value={ratioFormData.min_per_store}
                onChange={(e) =>
                  setRatioFormData((prev) => ({
                    ...prev,
                    min_per_store: parseInt(e.target.value) || 0,
                  }))
                }
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('tester.maxPerStore')}
                type="number"
                fullWidth
                value={ratioFormData.max_per_store}
                onChange={(e) =>
                  setRatioFormData((prev) => ({
                    ...prev,
                    max_per_store: parseInt(e.target.value) || 1,
                  }))
                }
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('tester.priority')}
                type="number"
                fullWidth
                value={ratioFormData.priority}
                onChange={(e) =>
                  setRatioFormData((prev) => ({
                    ...prev,
                    priority: parseInt(e.target.value) || 1,
                  }))
                }
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ratioFormData.is_active}
                    onChange={(e) =>
                      setRatioFormData((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                }
                label={t('tester.active')}
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRatioDialog}>{t('tester.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSaveRatio}
            disabled={
              !ratioFormData.category ||
              createRatioMutation.isPending ||
              updateRatioMutation.isPending
            }
          >
            {createRatioMutation.isPending || updateRatioMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('tester.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Delete Ratio Confirmation Dialog                                   */}
      {/* ================================================================= */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('tester.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('tester.confirmDeleteMessage')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('tester.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteRatio}
            disabled={deleteRatioMutation.isPending}
          >
            {deleteRatioMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('tester.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Snackbar notifications                                            */}
      {/* ================================================================= */}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {notification ? (
          <Alert
            onClose={() => setNotification(null)}
            severity={notification.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default TesterManagementDashboard;
